import React, { useCallback, useMemo, useState } from 'react';

import { DialogCustom } from '../../common/dialog-custom';
import { OptionsList } from '../shared/options-list';
import { useEditorKeyboard } from '../shared/use-editor-keyboard';

import type Handsontable from 'handsontable';

import { type SearchOption } from '@/types/search';

/**
 * Component Editor Props interface for Handsontable editor integration
 * Extended to support dropdown options
 */
interface DropdownEditorProps {
  modelValue: { value: unknown };
  onUpdateModelValue: (newValue: string) => void;
  onFinished: () => void;
  onEnterCommit?: (event: KeyboardEvent) => void;
  /** Used with Handsontable wrapper so document-capture Enter does not block opening the editor. */
  handsontable?: Handsontable.Core;
  cellElement?: HTMLElement;
  options?: string[]; // Dropdown options (array of strings)
  canEnterAnotherValue?: boolean; // Whether user can enter values not in the list
}

/**
 * Dropdown Editor Component for Handsontable
 * Compatible with ComponentEditorProps interface
 * This component is used with createReactComponentEditorClass
 * Similar to AutocompleteEditor, uses DialogCustom for proper positioning
 *
 * Behavior:
 * - User types directly in the cell (TEXTAREA) - for direct input or navigation
 * - This component shows ALL options list (no filtering - enum-based values)
 * - Clicking an option sets the value and closes the editor
 * - If canEnterAnotherValue is true, user can type any value
 * - If canEnterAnotherValue is false, user can only select from options
 */
export const DropdownEditor: React.FC<DropdownEditorProps> = ({
  modelValue,
  onUpdateModelValue,
  onFinished,
  onEnterCommit,
  handsontable,
  cellElement,
  options = [],
  canEnterAnotherValue = false,
}) => {
  // Get value from modelValue (text in cell)
  const value = typeof modelValue?.value === 'string' ? modelValue.value : '';

  // Always show all options - no filtering needed for enum-based dropdowns
  // User can type in cell for direct input, but options list always shows all values
  const filteredOptions = useMemo(() => {
    return options;
  }, [options]);

  // Check if current value is in the options list
  const selectedOption = useMemo(() => {
    if (!value.trim() || options.length === 0) {
      return null;
    }

    const trimmedValue = value.trim();
    const option = options.find((opt) => String(opt) === trimmedValue);
    return option === undefined ? null : option || null;
  }, [value, options]);

  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const selectedOptionIndex = useMemo(
    () => filteredOptions.findIndex((opt) => String(opt) === String(selectedOption)),
    [filteredOptions, selectedOption]
  );

  /**
   * Handle option click - set value and close editor
   */
  const handleOptionClick = useCallback(
    (option: string | SearchOption) => {
      // Update modelValue through editor callback
      const optionStr = typeof option === 'string' ? option : option.label;
      onUpdateModelValue(optionStr);
      // Finish editing - this will trigger Handsontable's afterChange
      onFinished();
    },
    [onUpdateModelValue, onFinished]
  );

  /**
   * Handle Enter key to save current input
   * If canEnterAnotherValue is false, only allow values from options
   */
  const handleEnterKey = useCallback(
    (event: KeyboardEvent) => {
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        const highlightedOption = filteredOptions.slice(highlightedIndex, highlightedIndex + 1)[0];
        handleOptionClick(highlightedOption as string | SearchOption);
        return;
      }

      if (!canEnterAnotherValue) {
        // If strict mode, only allow if value matches an option
        const trimmedValue = value.trim();
        const isValidOption = options.some((opt) => String(opt) === trimmedValue);
        if (!isValidOption && trimmedValue !== '') {
          // Value is not in options, don't save
          return;
        }
      }
      if (onEnterCommit) {
        onEnterCommit(event);
      } else {
        onFinished();
      }
    },
    [
      highlightedIndex,
      filteredOptions,
      handleOptionClick,
      value,
      options,
      canEnterAnotherValue,
      onEnterCommit,
      onFinished,
    ]
  );

  const handleArrowDownKey = useCallback(() => {
    if (filteredOptions.length === 0) return;
    setHighlightedIndex((prev) => {
      if (prev < 0) {
        return selectedOptionIndex >= 0 ? selectedOptionIndex : 0;
      }
      return Math.min(prev + 1, filteredOptions.length - 1);
    });
  }, [filteredOptions.length, selectedOptionIndex]);

  const handleArrowUpKey = useCallback(() => {
    if (filteredOptions.length === 0) return;
    setHighlightedIndex((prev) => {
      if (prev < 0) {
        return selectedOptionIndex >= 0 ? selectedOptionIndex : 0;
      }
      return Math.max(prev - 1, 0);
    });
  }, [filteredOptions.length, selectedOptionIndex]);

  // Handle keyboard events
  useEditorKeyboard({
    onFinished,
    onEnterKey: handleEnterKey,
    onArrowDownKey: handleArrowDownKey,
    onArrowUpKey: handleArrowUpKey,
    handsontable,
  });

  const cellWidth = cellElement?.getBoundingClientRect().width || 120;
  // Dropdown popup can be smaller than autocomplete (options are usually shorter)
  const minWidth = Math.max(cellWidth, 120);

  return (
    <DialogCustom
      cellElement={cellElement}
      onFinished={onFinished}
      minWidth={minWidth}
      padding='8px'
    >
      <OptionsList
        options={filteredOptions}
        selectedValue={selectedOption}
        onOptionClick={handleOptionClick}
        highlightedIndex={highlightedIndex}
        maxHeight='250px'
        fontSize='13px'
        paddingY={0.75}
        paddingX={1.5}
        getOptionKey={(option, index) => `${String(option)}-${index}`}
        getOptionLabel={(option) => String(option)}
        isOptionSelected={(option, selected) => {
          return String(option) === String(selected);
        }}
      />
    </DialogCustom>
  );
};
